import { ButtonTertiary } from "@nova/component-btn"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { useMemo } from "react"
import { useParams } from "react-router"
import { Card } from "../../MetricLayout"
import { useLinkedLayers, useProjectLayers, useRemoveLinkedLayer } from "./LinkedLayerQueries"
import { selectFireById } from "features/entity-caching/fires/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import * as Icons from "@nova/component-icons"
import { sidePanelLayerTypeByProtobufEntityType, EntityType } from "@nova/util-side-panel"
import { getSidePanelLayerTypeToPrimaryIconMap } from "plugins/plugin-registry"
import { useRouter } from "pages"
import { Status } from "@nova/ds-component-status"
import { LinkLayersModal, useHandleLinkLayersSubmission } from "./LinkLayersModal"
import { Tag } from "@nova/ds-component-tag"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { pluralize } from "@nova/util-strings"

export const LinkedLayersCard = () => {
    const modal = useConfirmationModalContext()
    const { missionId } = useParams<{ missionId: string }>()
    const { isEditor } = useWorkspacePermissions()
    const { data: linkedLayers } = useLinkedLayers(missionId ?? "")
    const { removeLinkedLayer } = useRemoveLinkedLayer(missionId ?? "")
    const handleLinkLayers = useHandleLinkLayersSubmission()

    const groupedByProject = useMemo(() => {
        const map = new Map<string, string[]>()
        for (const layer of linkedLayers ?? []) {
            const existing = map.get(layer.projectId) ?? []
            map.set(layer.projectId, [...existing, layer.layerId])
        }
        return Array.from(map.entries()).map(([projectId, layerIds]) => ({ projectId, layerIds }))
    }, [linkedLayers])

    const projectCount = groupedByProject.length
    const layerCount = linkedLayers?.length ?? 0

    return (
        <Card className="flex flex-1">
            <div className="flex flex-col gap-4 w-48 shrink-0">
                <div className="flex flex-col gap-2">
                    <h2 className="text-white telegraf">Linked Layers</h2>
                    {layerCount > 0 && (
                        <div className="flex flex-col gap-0.5">
                            <small className="text-white">{projectCount} projects referenced</small>
                            <small className="text-white">{layerCount} layers linked</small>
                        </div>
                    )}
                </div>
                {!!layerCount && isEditor && (
                    <div>
                        <ButtonTertiary
                            title="Link more layers"
                            onClick={() => {
                                void modal.confirm<{ projectId: string; layerIds: string[] }>({
                                    RenderComponent: LinkLayersModal,
                                    asyncConfirmCallback: handleLinkLayers,
                                })
                            }}
                            RightIcon={Icons.Link}
                        />
                    </div>
                )}
            </div>
            {!!groupedByProject.length ? (
                <>
                    <div className="w-px bg-gray-700 self-stretch" />
                    <div className="flex flex-col divide-y divide-gray-700 flex-1 pl-10">
                        {groupedByProject.map(({ projectId, layerIds }) => (
                            <ProjectLayerGroup
                                key={projectId}
                                projectId={projectId}
                                linkedLayerIds={layerIds}
                                onRemoveLayer={(layerId) => removeLinkedLayer(layerId)}
                            />
                        ))}
                    </div>
                </>
            ) : (
                <div className="flex justify-between items-center w-full gap-2">
                    <div className="text-gray-400">
                        Link project layers to give this flight spatial context and analysis
                    </div>
                    {isEditor && (
                        <ButtonTertiary
                            title="Link Layers"
                            onClick={() => {
                                void modal.confirm<{ projectId: string; layerIds: string[] }>({
                                    RenderComponent: LinkLayersModal,
                                    asyncConfirmCallback: handleLinkLayers,
                                })
                            }}
                            RightIcon={Icons.Link}
                            className="shrink-0"
                        />
                    )}
                </div>
            )}
        </Card>
    )
}

const ProjectLayerGroup = ({
    projectId,
    linkedLayerIds,
    onRemoveLayer,
}: {
    projectId: string
    linkedLayerIds: string[]
    onRemoveLayer: (layerId: string) => void
}) => {
    const router = useRouter("/workspace/:workspaceId")
    const { isEditor } = useWorkspacePermissions()
    const project = useAppSelector(selectFireById(projectId))
    const { data: allProjectLayers } = useProjectLayers(projectId)

    const projectName = (project?.name || "") as string
    const linkedSet = new Set(linkedLayerIds)
    const linkedLayerDetails = (allProjectLayers ?? []).filter((l) => linkedSet.has(l.id))

    return (
        <div className="flex flex-col gap-3 py-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-white font-medium">{projectName || projectId}</span>
                    <Status type="neutral" variant="outline">
                        {pluralize({ count: linkedLayerIds.length, str: "layer" })}
                    </Status>
                </div>
                <router.Link to={{ path: "/project/:projectId", params: { projectId } }}>
                    <div className="flex items-center gap-1 text-sm text-gray-100 hover:text-white transition-colors cursor-pointer">
                        Open Project
                        <Icons.SmallIcon Icon={Icons.ArrowRight} />
                    </div>
                </router.Link>
            </div>
            <div className="flex flex-wrap gap-2">
                {!!linkedLayerDetails.length &&
                    linkedLayerDetails.map((layer) => (
                        <Tag
                            key={layer.id}
                            onRemove={isEditor ? () => onRemoveLayer(layer.id) : undefined}
                        >
                            <div className="flex items-center gap-1">
                                <Icons.ExtraSmallIcon
                                    Icon={
                                        getSidePanelLayerTypeToPrimaryIconMap()[
                                            sidePanelLayerTypeByProtobufEntityType[
                                                layer.type as EntityType
                                            ]
                                        ]
                                    }
                                />
                                <small className="text-sm">{layer.name}</small>
                            </div>
                        </Tag>
                    ))}
            </div>
        </div>
    )
}
